/**
 * Pure utility functions for the Assignments tab.
 *
 * All functions in this module are pure: they take a
 * `StationAssignmentHalfData` (or related input) and return a new value
 * without mutating the input. This makes the assignment logic trivially
 * unit-testable and amenable to property-based testing.
 *
 * The authoritative location for an associate is the
 * `processPaths[*].workstations[*].assignedAssociates`
 * structure. The `assignedWorkstationId` field on entries in
 * `response.associates` is a convenience marker kept in sync by these
 * helpers.
 */

import {
  AssignmentAssociate,
  AssignmentsMetrics,
  StationAssignmentHalfData,
  ProcessPath,
  PathType,
  Workstation,
} from './models';

const PRIMARY_PATH_NAME = 'Primary';
const CLEAN_DECANT_PATH_NAME = 'Clean decant';
const RESALE_PATH_NAME = 'Resale';
const PROBLEM_SOLVE_PATH_NAME = 'Problem solve';

/**
 * Return `true` if the given associate currently appears in any workstation
 * or indirect-path list across the provided `processPaths`.
 *
 * Used by the AssociateSidebar to compute the unassigned pool:
 *   `response.associates.filter(a => !isAssigned(a, response.processPaths))`.
 */
export function isAssigned(
  associate: AssignmentAssociate,
  processPaths: ProcessPath[]
): boolean {
  for (const path of processPaths) {
    for (const ws of path.workstations) {
      if (ws.assignedAssociates.some(a => a.associateId === associate.associateId)) {
        return true;
      }
    }
    if (path.indirectAssociates?.some(a => a.associateId === associate.associateId)) {
      return true;
    }
  }
  return false;
}

/**
 * Remove an associate from every workstation and every indirect-path list.
 *
 * Returns a new `processPaths` array and a flag indicating whether any
 * change was made. If nothing changed, the original `processPaths`
 * reference is returned so callers can skip further copying.
 */
function removeAssociateFromAllLocations(
  processPaths: ProcessPath[],
  associateId: string
): { processPaths: ProcessPath[]; changed: boolean } {
  let changed = false;

  const newPaths = processPaths.map(path => {
    let pathChanged = false;

    const newWorkstations = path.workstations.map(ws => {
      const filtered = ws.assignedAssociates.filter(a => a.associateId !== associateId);
      if (filtered.length !== ws.assignedAssociates.length) {
        pathChanged = true;
        return { ...ws, assignedAssociates: filtered };
      }
      return ws;
    });

    let newIndirect = path.indirectAssociates;
    if (path.indirectAssociates) {
      const filtered = path.indirectAssociates.filter(a => a.associateId !== associateId);
      if (filtered.length !== path.indirectAssociates.length) {
        pathChanged = true;
        newIndirect = filtered;
      }
    }

    if (pathChanged) {
      changed = true;
      return {
        ...path,
        workstations: newWorkstations,
        indirectAssociates: newIndirect,
      };
    }
    return path;
  });

  return { processPaths: changed ? newPaths : processPaths, changed };
}

/**
 * Find a workstation by id across the provided `processPaths`.
 */
function findWorkstationById(
  processPaths: ProcessPath[],
  workstationId: string
): Workstation | null {
  for (const path of processPaths) {
    for (const ws of path.workstations) {
      if (ws.workstationId === workstationId) {
        return ws;
      }
    }
  }
  return null;
}

/**
 * Assign an associate to the given workstation.
 *
 * Behavior:
 * - Pure: returns a new `StationAssignmentHalfData`; the input is not mutated.
 * - If `workstationId` does not exist, returns the input unchanged.
 * - If `associateId` does not exist in `response.associates` or any
 *   workstation, returns the input unchanged.
 * - If the associate is currently assigned elsewhere (a workstation or an
 *   indirect path), they are removed from that location first.
 * - `response.associates[i].assignedWorkstationId` is kept in sync with the
 *   authoritative path/workstation structure.
 */
export function assignAssociateToWorkstation(
  response: StationAssignmentHalfData,
  associateId: string,
  workstationId: string
): StationAssignmentHalfData {
  if (!findWorkstationById(response.processPaths, workstationId)) {
    return response;
  }

  // Try to find the associate in the unassigned pool first.
  let canonicalIdx = response.associates.findIndex(a => a.associateId === associateId);
  let associate: AssignmentAssociate | undefined;

  if (canonicalIdx !== -1) {
    associate = response.associates[canonicalIdx];
  } else {
    // Associate is not in the unassigned pool — look in workstations.
    for (const path of response.processPaths) {
      for (const ws of path.workstations) {
        const found = ws.assignedAssociates.find(a => a.associateId === associateId);
        if (found) {
          associate = found;
          break;
        }
      }
      if (associate) break;
    }
  }

  if (!associate) {
    return response;
  }

  // Remove from any prior location (workstation or indirect path).
  const { processPaths: pathsWithoutAssociate } = removeAssociateFromAllLocations(
    response.processPaths,
    associateId
  );

  // Build the updated associate entry with the new workstation id.
  const updatedAssociate: AssignmentAssociate = {
    ...associate,
    assignedWorkstationId: workstationId,
  };

  // Update the associates array: if found in pool, update in place; otherwise keep as is.
  const newAssociates = canonicalIdx !== -1
    ? response.associates.map((a, i) => i === canonicalIdx ? updatedAssociate : a)
    : [...response.associates];

  // Insert the associate into the target workstation.
  const newProcessPaths = pathsWithoutAssociate.map(path => ({
    ...path,
    workstations: path.workstations.map(ws =>
      ws.workstationId === workstationId
        ? { ...ws, assignedAssociates: [...ws.assignedAssociates, updatedAssociate] }
        : ws
    ),
  }));

  return {
    ...response,
    associates: newAssociates,
    processPaths: newProcessPaths,
  };
}

/**
 * Remove an associate from a specific workstation.
 *
 * `locationId` is the `workstationId` where the associate currently sits.
 *
 * Behavior:
 * - Pure: returns a new `StationAssignmentHalfData`; the input is not mutated.
 * - If the associate is not currently at the given location, returns the
 *   input unchanged.
 * - Clears `assignedWorkstationId` on the moved associate in
 *   `response.associates`.
 */
export function removeAssociateFromWorkstation(
  response: StationAssignmentHalfData,
  associateId: string,
  locationId: string
): StationAssignmentHalfData {
  let foundInWorkstation = false;

  for (const path of response.processPaths) {
    for (const ws of path.workstations) {
      if (
        ws.workstationId === locationId &&
        ws.assignedAssociates.some(a => a.associateId === associateId)
      ) {
        foundInWorkstation = true;
        break;
      }
    }
    if (foundInWorkstation) break;
  }

  if (!foundInWorkstation) {
    return response;
  }

  const newProcessPaths = response.processPaths.map(path => {
    const newWorkstations = path.workstations.map(ws =>
      ws.workstationId === locationId
        ? {
            ...ws,
            assignedAssociates: ws.assignedAssociates.filter(
              a => a.associateId !== associateId
            ),
          }
        : ws
    );

    return {
      ...path,
      workstations: newWorkstations,
    };
  });

  const newAssociates = response.associates.map(a =>
    a.associateId === associateId ? { ...a, assignedWorkstationId: undefined } : a
  );

  return {
    ...response,
    associates: newAssociates,
    processPaths: newProcessPaths,
  };
}

/**
 * Filter an array of associates by name using case-insensitive substring
 * matching on `associateName`.
 *
 * If `searchQuery` is empty or whitespace-only, the input array is returned
 * unchanged (the same reference).
 */
export function filterUnassignedAssociates(
  associates: AssignmentAssociate[],
  searchQuery: string
): AssignmentAssociate[] {
  if (!searchQuery || searchQuery.trim().length === 0) {
    return associates;
  }
  const needle = searchQuery.toLowerCase();
  return associates.filter(a => a.associateName.toLowerCase().includes(needle));
}

/**
 * Recompute the top-level `AssignmentsMetrics` object from the current
 * state of `response.associates` and `response.processPaths`.
 *
 * Does not mutate the input.
 */
export function recomputeMetrics(response: StationAssignmentHalfData): AssignmentsMetrics {
  let totalUnlocked = 0;
  let totalClockedIn = 0;
  for (const associate of response.associates) {
    if (associate.isClocked) {
      totalClockedIn += 1;
    } else {
      totalUnlocked += 1;
    }
  }

  let assignedPrimary = 0;
  let assignedCleanDecant = 0;
  let assignedResale = 0;
  let assignedProblemSolve = 0;
  let assignedIndirect = 0;

  for (const path of response.processPaths) {
    const assignedCount = path.workstations.reduce(
      (sum, ws) => sum + ws.assignedAssociates.length,
      0
    );

    switch (path.processPathName) {
      case PRIMARY_PATH_NAME:
        assignedPrimary += assignedCount;
        break;
      case CLEAN_DECANT_PATH_NAME:
        assignedCleanDecant += assignedCount;
        break;
      case RESALE_PATH_NAME:
        assignedResale += assignedCount;
        break;
      default:
        break;
    }

    if (path.pathType === PathType.INDIRECT) {
      if (path.processPathName === PROBLEM_SOLVE_PATH_NAME) {
        assignedProblemSolve += assignedCount;
      } else {
        assignedIndirect += assignedCount;
      }
    }
  }

  return {
    totalUnlocked,
    totalClockedIn,
    totalActual: response.associates.length,
    assignedPrimary,
    assignedProblemSolve,
    assignedResale,
    assignedCleanDecant,
    assignedIndirect,
  };
}
